/**
 * Messaging Service
 * Handles all messaging operations with Firebase
 * Updated to support messaging without connection requirements and file attachments
 */

import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';
import { getAuth } from 'firebase/auth';
import { createMessageNotification } from '../../../services/firebase/notificationService';
import messageFileService from './messageFileService';

/**
 * Get conversations for a user
 * 
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} Array of conversation objects
 */
const getUserConversations = async (userId) => {
  try {
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userId),
      orderBy('lastMessageTimestamp', 'desc')
    );
    
    const snapshot = await getDocs(conversationsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      lastMessageTimestamp: doc.data().lastMessageTimestamp instanceof Timestamp ? 
        doc.data().lastMessageTimestamp.toDate() : 
        new Date()
    }));
  } catch (error) {
    console.error('Error getting user conversations:', error);
    throw error;
  }
};

/**
 * Subscribe to user conversations
 * 
 * @param {string} userId - The user ID
 * @param {function} callback - Callback function for conversations updates
 * @returns {function} Unsubscribe function
 */
const subscribeToConversations = (userId, callback) => {
  const conversationsQuery = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId),
    orderBy('lastMessageTimestamp', 'desc')
  );
  
  return onSnapshot(conversationsQuery, (snapshot) => {
    const conversations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      lastMessageTimestamp: doc.data().lastMessageTimestamp instanceof Timestamp ? 
        doc.data().lastMessageTimestamp.toDate() : 
        new Date()
    }));
    
    callback(conversations);
  }, (error) => {
    console.error('Error subscribing to conversations:', error);
  });
};

/**
 * Get messages for a conversation
 * 
 * @param {string} conversationId - The conversation ID
 * @param {number} limitCount - Number of messages to retrieve
 * @returns {Promise<Array>} Array of message objects
 */
const getConversationMessages = async (conversationId, limitCount = 50) => {
  try {
    const messagesQuery = query(
      collection(db, `conversations/${conversationId}/messages`),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(messagesQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp instanceof Timestamp ? 
        doc.data().timestamp.toDate() : 
        new Date()
    })).reverse(); // Reverse to get chronological order
  } catch (error) {
    console.error('Error getting conversation messages:', error);
    throw error;
  }
};

/**
 * Subscribe to messages in a conversation
 * 
 * @param {string} conversationId - The conversation ID
 * @param {function} callback - Callback function for messages updates
 * @param {number} limitCount - Number of messages to retrieve
 * @returns {function} Unsubscribe function
 */
const subscribeToMessages = (conversationId, callback, limitCount = 50) => {
  const messagesQuery = query(
    collection(db, `conversations/${conversationId}/messages`),
    orderBy('timestamp', 'asc'),
    limit(limitCount)
  );
  
  return onSnapshot(messagesQuery, (snapshot) => {
    const messages = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp instanceof Timestamp ? 
        doc.data().timestamp.toDate() : 
        new Date()
    }));
    
    callback(messages);
  }, (error) => {
    console.error('Error subscribing to messages:', error);
  });
};

/**
 * Check if current user is blocked by the other participant
 * 
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<boolean>} Whether current user is blocked
 */
const isCurrentUserBlocked = async (conversationId) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return false;
    }
    
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnap = await getDoc(conversationRef);
    
    if (!conversationSnap.exists()) {
      return false;
    }
    
    const conversationData = conversationSnap.data();
    const otherParticipantId = conversationData.participants.find(id => id !== currentUser.uid);
    
    return conversationData.securitySettings?.[otherParticipantId]?.hasBlocked && 
           conversationData.securitySettings?.[otherParticipantId]?.blockedUserId === currentUser.uid;
    
  } catch (error) {
    console.error('Error checking if user is blocked:', error);
    return false;
  }
};

/**
 * Send a message with optional file attachment
 * 
 * @param {string} conversationId - The conversation ID
 * @param {string} content - Message content (can be empty for file-only messages)
 * @param {File} file - File to attach (optional)
 * @param {string} type - Message type (text, image, document, audio)
 * @returns {Promise<string>} Message ID
 */
const sendMessageWithFile = async (conversationId, content, file = null, type = 'text') => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to send messages');
    }
    
    // Check if current user is blocked
    const isBlocked = await isCurrentUserBlocked(conversationId);
    if (isBlocked) {
      throw new Error('You cannot send messages to this user');
    }
    
    // Get conversation to find recipient and check connection status
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnap = await getDoc(conversationRef);
    
    if (!conversationSnap.exists()) {
      throw new Error('Conversation not found');
    }
    
    const conversationData = conversationSnap.data();
    const recipientId = conversationData.participants.find(id => id !== currentUser.uid);
    const isConnected = conversationData.connectionStatus?.isConnected || false;
    
    // Generate message ID for file upload
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    let fileAttachment = null;
    
    // Upload file if provided
    if (file) {
      const fileCategory = messageFileService.getFileCategory(file.type);
      fileAttachment = await messageFileService.uploadMessageFile(
        file, 
        conversationId, 
        messageId, 
        fileCategory
      );
      
      // Update message type based on file
      if (fileCategory === 'image') {
        type = 'image';
      } else if (fileCategory === 'audio') {
        type = 'audio';
      } else {
        type = 'file';
      }
    }
    
    // Create message data
    const messageData = {
      senderId: currentUser.uid,
      content: content || '',
      type,
      timestamp: serverTimestamp(),
      isRead: false,
      isFromUnconnectedUser: !isConnected
    };
    
    // Add file attachment if present
    if (fileAttachment) {
      messageData.attachment = fileAttachment;
    }
    
    // Add message to conversation
    const messageRef = await addDoc(
      collection(db, `conversations/${conversationId}/messages`), 
      messageData
    );
    
    // Prepare last message content for conversation update
    let lastMessageContent = content;
    if (fileAttachment && !content) {
      // If no text content, use file name as last message
      const fileIcon = type === 'image' ? '🖼️' : type === 'audio' ? '🎵' : '📎';
      lastMessageContent = `${fileIcon} ${fileAttachment.name}`;
    } else if (fileAttachment && content) {
      // If both text and file, show both
      lastMessageContent = `${content} 📎`;
    }
    
    // Update conversation with last message
    await updateDoc(conversationRef, {
      lastMessage: {
        content: lastMessageContent.length > 50 ? 
          lastMessageContent.substring(0, 50) + '...' : lastMessageContent,
        senderId: currentUser.uid,
        type,
        isFromUnconnectedUser: !isConnected,
        hasAttachment: !!fileAttachment
      },
      lastMessageTimestamp: serverTimestamp(),
      [`unreadCount.${recipientId}`]: conversationData.unreadCount?.[recipientId] ? 
        conversationData.unreadCount[recipientId] + 1 : 1
    });
    
    // Get sender info for notification
    const senderProfileRef = doc(db, 'profiles', currentUser.uid);
    const senderProfileSnap = await getDoc(senderProfileRef);
    
    if (senderProfileSnap.exists()) {
      const senderProfile = senderProfileSnap.data();
      
      // Create notification content based on message type
      let notificationContent = content;
      if (fileAttachment && !content) {
        notificationContent = `Sent a ${fileAttachment.fileType}`;
      } else if (fileAttachment && content) {
        notificationContent = `${content} (with attachment)`;
      }
      
      // Create notification for recipient
      const notificationType = isConnected ? 'message' : 'message_from_unconnected_user';
      
      await createMessageNotification(
        recipientId,
        currentUser.uid,
        senderProfile.basicInfo?.name || 'User',
        senderProfile.photos && senderProfile.photos.length > 0 ? senderProfile.photos[0] : null,
        notificationContent,
        notificationType
      );
    }
    
    return messageRef.id;
  } catch (error) {
    console.error('Error sending message with file:', error);
    throw error;
  }
};

/**
 * Enhanced send message function that checks for blocks and connection status
 * 
 * @param {string} conversationId - The conversation ID
 * @param {string} content - Message content
 * @param {string} type - Message type (text, image, etc.)
 * @returns {Promise<string>} Message ID
 */
const sendMessage = async (conversationId, content, type = 'text') => {
  return sendMessageWithFile(conversationId, content, null, type);
};

/**
 * Delete a message and its attachments
 * 
 * @param {string} conversationId - The conversation ID
 * @param {string} messageId - The message ID
 * @returns {Promise<void>}
 */
const deleteMessage = async (conversationId, messageId) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to delete messages');
    }
    
    // Get message to check if user is sender
    const messageRef = doc(db, `conversations/${conversationId}/messages`, messageId);
    const messageSnap = await getDoc(messageRef);
    
    if (!messageSnap.exists()) {
      throw new Error('Message not found');
    }
    
    const messageData = messageSnap.data();
    
    if (messageData.senderId !== currentUser.uid) {
      throw new Error('You can only delete your own messages');
    }
    
    // Delete file attachment if present
    if (messageData.attachment && messageData.attachment.storagePath) {
      try {
        await messageFileService.deleteMessageFile(messageData.attachment.storagePath);
      } catch (error) {
        console.error('Error deleting file attachment:', error);
        // Continue with message deletion even if file deletion fails
      }
    }
    
    // Delete message document
    await deleteDoc(messageRef);
    
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};

/**
 * Mark messages as read
 * 
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<void>}
 */
const markMessagesAsRead = async (conversationId) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to mark messages as read');
    }
    
    // Update conversation unread count
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      [`unreadCount.${currentUser.uid}`]: 0
    });
    
    // Query unread messages
    const messagesQuery = query(
      collection(db, `conversations/${conversationId}/messages`),
      where('senderId', '!=', currentUser.uid),
      where('isRead', '==', false)
    );
    
    const messagesSnapshot = await getDocs(messagesQuery);
    
    // Update each message
    const updatePromises = messagesSnapshot.docs.map(messageDoc => 
      updateDoc(messageDoc.ref, { isRead: true })
    );
    
    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error marking messages as read:', error);
    throw error;
  }
};

/**
 * Create a new conversation with a user (enhanced for unconnected users)
 * 
 * @param {string} recipientId - Recipient user ID
 * @param {boolean} isConnected - Whether users are connected
 * @returns {Promise<string>} Conversation ID
 */
const createConversation = async (recipientId, isConnected = false) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to start a conversation');
    }
    
    // Create conversation ID (sorted user IDs to ensure uniqueness)
    const participants = [currentUser.uid, recipientId].sort();
    const conversationId = participants.join('_');
    
    // Check if conversation already exists
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnap = await getDoc(conversationRef);
    
    if (conversationSnap.exists()) {
      return conversationId;
    }
    
    // Get profiles for both users
    const [currentUserProfileSnap, recipientProfileSnap] = await Promise.all([
      getDoc(doc(db, 'profiles', currentUser.uid)),
      getDoc(doc(db, 'profiles', recipientId))
    ]);
    
    const currentUserProfile = currentUserProfileSnap.exists() ? currentUserProfileSnap.data() : {};
    const recipientProfile = recipientProfileSnap.exists() ? recipientProfileSnap.data() : {};
    
    // Create conversation with enhanced metadata
    await setDoc(conversationRef, {
      participants,
      participantsInfo: {
        [currentUser.uid]: {
          name: currentUserProfile.basicInfo?.name || 'User',
          photo: currentUserProfile.photos && currentUserProfile.photos.length > 0 ? currentUserProfile.photos[0] : null,
          username: currentUserProfile.basicInfo?.username || null
        },
        [recipientId]: {
          name: recipientProfile.basicInfo?.name || 'User',
          photo: recipientProfile.photos && recipientProfile.photos.length > 0 ? recipientProfile.photos[0] : null,
          username: recipientProfile.basicInfo?.username || null
        }
      },
      lastMessage: null,
      lastMessageTimestamp: serverTimestamp(),
      createdAt: serverTimestamp(),
      unreadCount: {
        [currentUser.uid]: 0,
        [recipientId]: 0
      },
      // New fields for connection tracking
      connectionStatus: {
        isConnected: isConnected,
        lastChecked: serverTimestamp()
      },
      // Track who started the conversation
      initiatedBy: currentUser.uid,
      // Security settings per user
      securitySettings: {
        [currentUser.uid]: {
          hasBlocked: false,
          hasReported: false
        },
        [recipientId]: {
          hasBlocked: false,
          hasReported: false,
          receivedUnconnectedMessage: false
        }
      }
    });
    
    return conversationId;
  } catch (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }
};

/**
 * Block a user in a conversation
 * 
 * @param {string} conversationId - The conversation ID
 * @param {string} userIdToBlock - The user ID to block
 * @returns {Promise<void>}
 */
const blockUserInConversation = async (conversationId, userIdToBlock) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to block users');
    }
    
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      [`securitySettings.${currentUser.uid}.hasBlocked`]: true,
      [`securitySettings.${currentUser.uid}.blockedUserId`]: userIdToBlock,
      [`securitySettings.${currentUser.uid}.blockedAt`]: serverTimestamp()
    });
    
  } catch (error) {
    console.error('Error blocking user:', error);
    throw error;
  }
};

/**
 * Unblock a user in a conversation
 * 
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<void>}
 */
const unblockUserInConversation = async (conversationId) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to unblock users');
    }
    
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      [`securitySettings.${currentUser.uid}.hasBlocked`]: false,
      [`securitySettings.${currentUser.uid}.blockedUserId`]: null,
      [`securitySettings.${currentUser.uid}.unblockedAt`]: serverTimestamp()
    });
    
  } catch (error) {
    console.error('Error unblocking user:', error);
    throw error;
  }
};

/**
 * Mark that user has been notified about unconnected message
 * 
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<void>}
 */
const markUnconnectedMessageNotified = async (conversationId) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in');
    }
    
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      [`securitySettings.${currentUser.uid}.receivedUnconnectedMessage`]: true,
      [`securitySettings.${currentUser.uid}.notifiedAt`]: serverTimestamp()
    });
    
  } catch (error) {
    console.error('Error marking unconnected message notified:', error);
    throw error;
  }
};

/**
 * Update connection status in conversation
 * 
 * @param {string} conversationId - The conversation ID
 * @param {boolean} isConnected - Whether users are now connected
 * @returns {Promise<void>}
 */
const updateConversationConnectionStatus = async (conversationId, isConnected) => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      'connectionStatus.isConnected': isConnected,
      'connectionStatus.lastChecked': serverTimestamp()
    });
    
  } catch (error) {
    console.error('Error updating connection status:', error);
    throw error;
  }
};

/**
 * Get the total count of unread messages across all conversations
 * 
 * @returns {Promise<number>} Total unread count
 */
const getTotalUnreadCount = async () => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return 0;
    }
    
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', currentUser.uid)
    );
    
    const snapshot = await getDocs(conversationsQuery);
    
    let totalUnread = 0;
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.unreadCount && data.unreadCount[currentUser.uid]) {
        totalUnread += data.unreadCount[currentUser.uid];
      }
    });
    
    return totalUnread;
  } catch (error) {
    console.error('Error getting total unread count:', error);
    return 0;
  }
};

/**
 * Subscribe to total unread count
 * 
 * @param {function} callback - Callback function for unread count updates
 * @returns {function} Unsubscribe function
 */
const subscribeToUnreadCount = (callback) => {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    callback(0);
    return () => {};
  }
  
  const conversationsQuery = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', currentUser.uid)
  );
  
  return onSnapshot(conversationsQuery, (snapshot) => {
    let totalUnread = 0;
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.unreadCount && data.unreadCount[currentUser.uid]) {
        totalUnread += data.unreadCount[currentUser.uid];
      }
    });
    
    callback(totalUnread);
  }, (error) => {
    console.error('Error subscribing to unread count:', error);
  });
};

/**
 * Check if a conversation has unconnected message warnings that need to be shown
 * 
 * @param {Object} conversation - Conversation object
 * @param {string} currentUserId - Current user ID
 * @returns {boolean} Whether to show unconnected user warning
 */
const shouldShowUnconnectedWarning = (conversation, currentUserId) => {
  if (!conversation || !currentUserId) return false;
  
  // Don't show if users are connected
  if (conversation.connectionStatus?.isConnected) return false;
  
  // Don't show if user has already been notified
  if (conversation.securitySettings?.[currentUserId]?.receivedUnconnectedMessage) return false;
  
  // Show if there are messages and users aren't connected
  return conversation.lastMessage != null;
};

/**
 * Get conversation security info for current user
 * 
 * @param {Object} conversation - Conversation object
 * @param {string} currentUserId - Current user ID
 * @returns {Object} Security information
 */
const getConversationSecurityInfo = (conversation, currentUserId) => {
  if (!conversation || !currentUserId) {
    return {
      isBlocked: false,
      hasBlocked: false,
      isConnected: false,
      shouldShowWarning: false
    };
  }
  
  const otherParticipantId = conversation.participants?.find(id => id !== currentUserId);
  const currentUserSecurity = conversation.securitySettings?.[currentUserId] || {};
  const otherUserSecurity = conversation.securitySettings?.[otherParticipantId] || {};
  
  return {
    isBlocked: otherUserSecurity.hasBlocked && otherUserSecurity.blockedUserId === currentUserId,
    hasBlocked: currentUserSecurity.hasBlocked,
    isConnected: conversation.connectionStatus?.isConnected || false,
    shouldShowWarning: shouldShowUnconnectedWarning(conversation, currentUserId),
    otherParticipantId
  };
};

const messagingService = {
  getUserConversations,
  subscribeToConversations,
  getConversationMessages,
  subscribeToMessages,
  sendMessage,
  sendMessageWithFile,
  deleteMessage,
  markMessagesAsRead,
  createConversation,
  getTotalUnreadCount,
  subscribeToUnreadCount,
  blockUserInConversation,
  unblockUserInConversation,
  markUnconnectedMessageNotified,
  updateConversationConnectionStatus,
  isCurrentUserBlocked,
  shouldShowUnconnectedWarning,
  getConversationSecurityInfo
};

export default messagingService;